/**
 * VALIDADORES CENTRALIZADOS
 * Proporciona validación y sanitización uniforme para todos los formularios
 * Reduce duplicación de código y asegura consistencia en validaciones
 */

import React from 'react';

export const fieldValidators = {
    email: {
        regex: /^[^\s@]{1,64}@[^\s@]{1,255}\.[^\s@]{2,}$/,
        message: 'Email inválido (ej: user@example.com)',
        sanitize: (v) => v.toLowerCase().trim()
    },

    phone: {
        regex: /^\+?[\d\-\s()]{10,20}$/,
        message: 'Teléfono debe tener 10-20 dígitos',
        sanitize: (v) => v.replace(/\s/g, '').trim()
    },

    rfc: {
        regex: /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/,
        message: 'RFC inválido (ej: ABC123456DEF)',
        sanitize: (v) => v.toUpperCase().trim()
    },

    name: {
        regex: /^[a-záéíóúàâäñ\s\-']{2,100}$/i,
        message: 'Nombre debe tener 2-100 caracteres, sin números ni caracteres especiales',
        sanitize: (v) => v.trim()
    },

    vin: {
        regex: /^[A-HJ-NPR-Z0-9]{17}$/,
        message: 'VIN debe ser 17 caracteres alfanuméricos',
        sanitize: (v) => v.toUpperCase().trim()
    },

    // Campos numéricos
    positiveNumber: {
        regex: /^\d+(\.\d{1,2})?$/,
        message: 'Debe ser un número positivo',
        sanitize: (v) => v.trim()
    },

    // Contraseña - mínimo 8 caracteres, al menos 1 mayúscula, 1 número
    password: {
        regex: /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
        message: 'Contraseña debe tener: 8+ caracteres, 1 mayúscula, 1 número, 1 símbolo',
        sanitize: (v) => v.trim()
    },

    // Alfanuméricos solo
    alphanumeric: {
        regex: /^[a-zA-Z0-9\s]{2,100}$/,
        message: 'Solo letras, números y espacios permitidos',
        sanitize: (v) => v.trim()
    },

    // Direcciones
    address: {
        regex: /^[a-zA-Z0-9\s\-#,.áéíóúñ]{5,200}$/,
        message: 'Dirección inválida (5-200 caracteres)',
        sanitize: (v) => v.trim()
    },

    // URLs
    url: {
        regex: /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/,
        message: 'URL inválida',
        sanitize: (v) => v.toLowerCase().trim()
    }
};

/**
 * Valida un campo individual
 * @param {string} fieldName - Nombre del campo (ej: 'email', 'phone')
 * @param {string} value - Valor a validar
 * @returns {object} { valid: boolean, error: string|null, sanitized: string }
 */
export function validateField(fieldName, value) {
    if (!fieldName || value === undefined || value === null) {
        return { valid: true, error: null, sanitized: value };
    }

    const validator = fieldValidators[fieldName];
    if (!validator) {
        console.warn(`Validator no encontrado para: ${fieldName}`);
        return { valid: true, error: null, sanitized: value };
    }

    try {
        const sanitized = validator.sanitize(String(value));
        const isValid = validator.regex.test(sanitized);

        return {
            valid: isValid,
            error: isValid ? null : validator.message,
            sanitized: isValid ? sanitized : value // Retorna valor original si no es válido
        };
    } catch (err) {
        console.error(`Error validando ${fieldName}:`, err);
        return { valid: false, error: 'Error en validación', sanitized: value };
    }
}

/**
 * Valida múltiples campos
 * @param {object} formData - Objeto con los datos del formulario
 * @param {array} fieldsToValidate - Array de nombres de campos a validar
 * @returns {object} { valid: boolean, errors: { [fieldName]: errorMsg } }
 */
export function validateForm(formData, fieldsToValidate) {
    const errors = {};
    let hasErrors = false;

    fieldsToValidate.forEach(fieldName => {
        const value = formData[fieldName];
        const validation = validateField(fieldName, value);

        if (!validation.valid) {
            errors[fieldName] = validation.error;
            hasErrors = true;
        }
    });

    return {
        valid: !hasErrors,
        errors
    };
}

/**
 * Sanitiza todos los campos de un objeto (para enviar al API)
 * @param {object} data - Objeto con los datos
 * @param {array} fieldNames - Array de nombres de campos a sanitizar
 * @returns {object} Objeto con campos sanitizados
 */
export function sanitizeFormData(data, fieldNames) {
    const sanitized = { ...data };

    fieldNames.forEach(fieldName => {
        if (data[fieldName] !== undefined && data[fieldName] !== null) {
            const validation = validateField(fieldName, data[fieldName]);
            sanitized[fieldName] = validation.sanitized;
        }
    });

    return sanitized;
}

/**
 * Hook para usar validación en formularios
 * Uso: const { validate, errors, setErrors } = useFormValidator();
 */
export function useFormValidator() {
    const [errors, setErrors] = React.useState({});

    const validate = React.useCallback((fieldName, value) => {
        const validation = validateField(fieldName, value);
        
        setErrors(prev => ({
            ...prev,
            [fieldName]: validation.error
        }));

        return validation;
    }, []);

    const validateAll = React.useCallback((formData, fieldsToValidate) => {
        const result = validateForm(formData, fieldsToValidate);
        setErrors(result.errors);
        return result.valid;
    }, []);

    const clearErrors = React.useCallback(() => {
        setErrors({});
    }, []);

    return {
        validate,
        validateAll,
        errors,
        setErrors,
        clearErrors
    };
}
